/* eslint-disable @tanstack/query/exhaustive-deps */
// PropAI-web specific queries (to be added as needed)

import { queryOptions } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { PropAIApi } from "../propai.api";

export const getPropAIOrganizationListQueryOptions = (propAiApi: PropAIApi | null) =>
  queryOptions({
    queryKey: ["organizations"],
    queryFn: () => propAiApi!.getOrganizationList(),
    enabled: !!propAiApi,
  });

export const acceptInviteOptions = (propAiApi: PropAIApi | null, inviteToken: string) =>
  queryOptions({
    queryKey: ["accept-invite", inviteToken],
    queryFn: () => propAiApi?.acceptInvitation(inviteToken),
    enabled: !!inviteToken,
    retry: false,
  });

export const getMeterDataOptions = (
  propAiApi: PropAIApi | null,
  substationUID: string | undefined,
  components: string[] = ["heat_energy", "returntemp", "supplytemp"],
  fromDate?: DateTime,
  toDate?: DateTime
) =>
  queryOptions({
    queryKey: [
      "substations",
      substationUID,
      "meter-data",
      components,
      fromDate?.toISO(),
      toDate?.toISO(),
    ],
    queryFn: () =>
      propAiApi?.getMeterData(substationUID!, components, fromDate?.toISO(), toDate?.toISO()),
    enabled: !!substationUID,
  });

export const getInfoBlockPerSubstation = (
  propAiApi: PropAIApi | null,
  substationUid?: string,
  blockNames?: string[]
) =>
  queryOptions({
    queryKey: ["info-blocks", substationUid, blockNames],
    queryFn: () => propAiApi?.getInfoBlocksPerSubstation(substationUid, blockNames),
    enabled: !!substationUid && blockNames && blockNames.length > 0,
  });

export const getFlexStationsPerSubstationOptions = (
  propAiApi: PropAIApi | null,
  substationUid?: string
) =>
  queryOptions({
    queryKey: ["flexstations", substationUid],
    queryFn: () => propAiApi?.getFlexStationsBySubstation(substationUid),
    enabled: !!substationUid,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: true,
    structuralSharing: true,
  });

export const getFlexstationClimateSeriesOptions = (
  propAiApi: PropAIApi | null,
  flexstationUid: string | null | undefined,
  fromDate?: DateTime | null,
  toDate?: DateTime | null
) =>
  queryOptions({
    queryKey: [
      "flexstations",
      flexstationUid,
      "climate-series",
      fromDate?.toISO(),
      toDate?.toISO(),
    ],
    queryFn: () =>
      propAiApi?.getFlexstationClimateSeries(
        flexstationUid!,
        fromDate?.toISO() ?? undefined,
        toDate?.toISO() ?? undefined
      ),
    enabled: !!propAiApi && !!flexstationUid && !!fromDate && !!toDate,
    staleTime: 5 * 60 * 1000,
  });

export const getFlexStationLiveInsightsOptions = (
  propAiApi: PropAIApi | null,
  flexstationUid?: string
) =>
  queryOptions({
    queryKey: ["flexstations", flexstationUid, "insights", "live"],
    queryFn: () => propAiApi?.getFlexStationLiveInsights(flexstationUid),
    enabled: !!flexstationUid && !!propAiApi,
  });

export const getUrgentNotificationsOptions = (
  propAiApi: PropAIApi | null,
  params: {
    status_filter?: string;
    substation_uid?: string;
    organization_uid?: string;
    network_uid?: string;
    limit?: number;
    offset?: number;
  }
) =>
  queryOptions({
    queryKey: [
      "notifications",
      "urgent",
      params.substation_uid,
      params.organization_uid,
      params.network_uid,
      params.status_filter,
      params.limit,
      params.offset,
    ],
    queryFn: () => propAiApi?.getUrgentNotifications(params),
    enabled: !!propAiApi && !!params.substation_uid,
  });

/**
 * Query options for fetching branding from the dedicated endpoint.
 * Returns null when no custom branding exists, letting BrandingProvider fall back to defaults.
 */
export const getBrandingQueryOptions = (
  propAiApi: PropAIApi | null,
  propertyOrganizationUid?: string
) =>
  queryOptions({
    queryKey: ["branding", propertyOrganizationUid],
    queryFn: () => propAiApi!.getBranding(propertyOrganizationUid!),
    enabled: !!propAiApi && !!propertyOrganizationUid,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

export const getPropertyOrganizationUsersOptions = (
  propAiApi: PropAIApi | null,
  propertyOrganizationUid?: string
) =>
  queryOptions({
    queryKey: ["admin", "property-organization", propertyOrganizationUid, "users"],
    queryFn: () => propAiApi?.getPropertyOrganizationUsers(propertyOrganizationUid),
    enabled: !!propertyOrganizationUid && !!propAiApi,
  });

export const addPropertyOrganizationUserMutation = {
  mutationFn: async ({
    propAiApi,
    propertyOrganizationUid,
    email,
    locale,
  }: {
    propAiApi: PropAIApi | null;
    propertyOrganizationUid: string;
    email: string;
    locale?: string;
  }) => {
    return propAiApi.addPropertyOrganizationUser(propertyOrganizationUid, email, locale);
  },
};

export const removePropertyOrganizationUserMutation = {
  mutationFn: async ({
    propAiApi,
    propertyOrganizationUid,
    userUid,
  }: {
    propAiApi: PropAIApi | null;
    propertyOrganizationUid: string;
    userUid: string;
  }) => {
    return propAiApi.removePropertyOrganizationUser(propertyOrganizationUid, userUid);
  },
};
